import { z } from "zod";
import { ReadyStartupSchema } from "./startup.js";
import { RoleInputSchema } from "./permissions.js";
import { AppSettingsSchema } from "./settings.js";
import { ClientRoomIdSchema, RoomPasswordProofSchema, RoomScheduleSchema, } from "./rooms.js";
// `default` is an input flag ("this role is the default") used to derive the
// stored `defaultRole` pointer — it is not part of the stored role shape.
const RoleSetupSchema = RoleInputSchema.extend({
    default: z.boolean().optional(),
});
export const RolesSetupSchema = z.record(z.string(), RoleSetupSchema);
/**
 * A single room defined locally during onboarding and shipped with the one-time
 * `/v1/setup` call. The client may choose the `roomId` (needed to derive a
 * `passwordProof`, as on `POST /v1/rooms/create`); without one it is minted
 * server-side. Ids must be distinct within the request and must not belong to
 * an existing room. `tempId` is a client-generated correlation id echoed back in
 * the response so the client can map each created room to its local entry (e.g.
 * to persist an e2e cipher password under the real `roomId`). `hasPassword`
 * reflects whether the client holds a cipher password for the room — the
 * password itself is never sent (e2e is client-side only).
 */
export const SetupRoomInputSchema = z.object({
    tempId: z.string().min(1).max(256),
    name: z.string().min(1).max(256),
    isPrivate: z.boolean().default(false),
    hasPassword: z.boolean().default(false),
    schedule: RoomScheduleSchema.optional(),
    roomId: ClientRoomIdSchema.optional(),
    passwordProof: RoomPasswordProofSchema.optional(),
});
/**
 * The room list submitted during onboarding. At least one room is required to
 * finish setup; capped at 50 to stay comfortably within Deno KV atomic-commit
 * limits when persisted alongside the app instance in a single transaction.
 */
export const SetupRoomsSchema = z.array(SetupRoomInputSchema).min(1).max(50);
export const SetupRequestSchema = z.object({
    settings: AppSettingsSchema,
    permissions: RolesSetupSchema,
});
export const SetupResponseSchema = z.object({
    startup: ReadyStartupSchema,
    rooms: z.array(SetupRoomInputSchema.omit({ passwordProof: true }).extend({
        roomId: z.string(),
    })),
});
